// Chat state store — lives outside ChatPanel.svelte so the conversation
// survives mode switches and panel toggles. ChatPanel reads/writes this
// directly instead of holding local state.
//
// History is mirrored to localStorage so it also survives page reloads.
// Pending image / busy / pipeline-stage are kept here too so the user can
// switch modes mid-request and still see the spinner / streaming response.

import type { PipelineStage } from './imagePipeline';

export type ChatMsg = {
  role: 'user' | 'assistant';
  content: string;
  actions?: string[];
  /** User-attached source photo (data URI). */
  imageDataUri?: string;
  /** Stage-1 generated multi-view sheet. */
  sheetDataUri?: string;
};

const HISTORY_KEY = 'clayforge.chat.history.v1';

function loadHistory(): ChatMsg[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function saveHistory(h: ChatMsg[]) {
  try {
    // Strip large data URIs from persisted copy — keeps localStorage under quota.
    const slim = h.map((m) => ({
      ...m,
      imageDataUri: m.imageDataUri ? '__stripped__' : undefined,
      sheetDataUri: m.sheetDataUri ? '__stripped__' : undefined
    }));
    localStorage.setItem(HISTORY_KEY, JSON.stringify(slim));
  } catch {
    // Quota exceeded or disabled — drop silently.
  }
}

export const chatStore = $state({
  history: loadHistory() as ChatMsg[],
  busy: false,
  /** Pipeline stage for in-flight image-mode requests. */
  pipelineStage: null as PipelineStage | null,
  /** Open/closed across all modes. Persisted via localStorage on toggle. */
  open: localStorage.getItem('clayforge.chat.open') === '1',
  lastError: ''
});

/** Set history and persist. ChatPanel calls this instead of mutating directly. */
export function setHistory(h: ChatMsg[]) {
  chatStore.history = h;
  saveHistory(h);
}

export function appendMessage(m: ChatMsg) {
  chatStore.history = [...chatStore.history, m];
  saveHistory(chatStore.history);
}

export function clearChatHistory() {
  chatStore.history = [];
  saveHistory([]);
}

export function setChatOpen(v: boolean) {
  chatStore.open = v;
  try { localStorage.setItem('clayforge.chat.open', v ? '1' : '0'); } catch { /* ignore */ }
}
